<template>
    <section>
        <component :is="view" v-bind="{ ...$data, view }" @emitEvent="eventEmitter" />
    </section>
</template>

<script>
import OperatorView from './views/OperatorView.vue'
import ModuleView from './views/ModuleView.vue'
import { mapGetters, mapActions } from "vuex";
export default {
    props: {
        view: {
            type: String,
            default: 'OperatorView'
        }
    },
    components: {
        OperatorView,
        ModuleView
    },
    data() {
        return {
            socketClient: null,
            connectionCheck: false,
            reconnection: false,

            socketConnectionData: {
                url: process.env.MIX_APP_URL,
                port: process.env.MIX_OR_PORT,
                user: process.env.MIX_OR_USERNAME,
                password: process.env.MIX_OR_PASSWORD,
                token: null,
                tokenRenewed: false,
            },

            enabledListeners: {
                queueCalls: false,
                currentCalls: false,
                queueMembers: false,
                peerStatus: false,
            },

            queueList: [],
            usersList: [],

            currentCalls: [],
            queueMembers: [],
            peersStatus: [],
            queueCalls: [],

            queueInfoShowing: false,
            queueCallsCount: 0,
            activePauses: {}
        }
    },
    computed: {
        ...mapGetters(["getStoreQueues", "getOperatorRealtime"]),
    },
    async created() {
        await this.setupSocketConnection()
    },
    async mounted() {
        this.fetchStoreQueues()
        const token = localStorage.getItem('realtimeToken')
        if (!token) {
            await this.getAPIToken()
        } else {
            this.socketConnectionData.token = token
        }

        await this.getUsersList()

        await this.getQueueCalls()
        await this.getCurrentCalls()
        await this.getQueueMembers()
        await this.getPeersStatus()
    },
    methods: {
        ...mapActions([
            'fetchStoreQueues',
            'sendOperatorPauseApiRealtime',
            'sendOperatorUnpause'
        ]),
        async getUsersList() {
            await axios.get('/listUsersRealtimePhone').then(({ data }) => {
                if (data && data.length) {
                    this.usersList = data
                }
            })
        },

        async getAPIToken(isRetry = false) {
            this.socketConnectionData.tokenRenewed = false

            try {
                const response = await fetch(`${this.socketConnectionData.url}:${this.socketConnectionData.port}/generate-token`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        username: this.socketConnectionData.user,
                        password: this.socketConnectionData.password,
                    }),
                    credentials: 'include',
                })

                const data = await response.json()

                // Verifica se o token foi retornado com sucesso
                if (data.token) {
                    this.socketConnectionData.token = data.token
                    localStorage.setItem('realtimeToken', data.token)
                    this.socketConnectionData.tokenRenewed = true
                    return data.token
                }

                // Se a resposta indicar token expirado
                if (data.message === 'Token expirado' && !isRetry) {
                    console.warn('Token expirado detectado. Tentando gerar novo token...')
                    // Remove o token expirado
                    localStorage.removeItem('realtimeToken')
                    this.socketConnectionData.token = null
                    // Tenta novamente (apenas uma vez para evitar loop infinito)
                    return await this.getAPIToken(true)
                }

                throw new Error(data.message || 'Erro ao gerar token')

            } catch (error) {
                console.error('Erro ao obter token:', error)

                // Se for erro de token expirado e ainda não tentou novamente
                if (error.message === 'Token expirado' && !isRetry) {
                    console.warn('Tentando renovar token expirado...')
                    localStorage.removeItem('realtimeToken')
                    this.socketConnectionData.token = null
                    return await this.getAPIToken(true)
                }

                throw error
            }
        },

        async getQueueCalls() {
            this.enabledListeners = { ...this.enabledListeners, queueCalls: false };
            fetch(`${this.socketConnectionData.url}:${this.socketConnectionData.port}/queue-entry`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.socketConnectionData.token}`
                },
                credentials: 'include',
            })
                .then(response => response.json())
                .then(async data => {
                    if (data.error) {
                        if (data.error.includes('expirado') || data.error.includes('ausente')) {
                            if (!this.socketConnectionData.tokenRenewed) {
                                await this.getAPIToken().then(async () => {
                                    await this.getQueueCalls()
                                })
                            }
                        }
                    } else {
                        this.queueCalls = data
                        this.enabledListeners = { ...this.enabledListeners, queueCalls: true };
                    }
                })
                .catch(error => console.error('Erro:', error));
        },

        async getCurrentCalls() {
            this.enabledListeners = { ...this.enabledListeners, currentCalls: false };
            fetch(`${this.socketConnectionData.url}:${this.socketConnectionData.port}/core-show-channels`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.socketConnectionData.token}`
                },
                credentials: 'include',
            })
                .then(response => response.json())
                .then(async data => {
                    if (data.error) {
                        if (data.error.includes('expirado') || data.error.includes('ausente')) {
                            if (!this.socketConnectionData.tokenRenewed) {
                                await this.getAPIToken().then(async () => {
                                    await this.getCurrentCalls()
                                })
                            }
                        }
                    } else {
                        this.currentCalls = data
                        this.enabledListeners = { ...this.enabledListeners, currentCalls: true };
                    }
                })
                .catch(error => console.error('Erro:', error));
        },

        async getQueueMembers() {
            this.enabledListeners = { ...this.enabledListeners, queueMembers: false };
            fetch(`${this.socketConnectionData.url}:${this.socketConnectionData.port}/queue-member`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.socketConnectionData.token}`
                },
                credentials: 'include',
            })
                .then(response => response.json())
                .then(async data => {
                    if (data.error) {
                        if (data.error.includes('expirado') || data.error.includes('ausente')) {
                            if (!this.socketConnectionData.tokenRenewed) {
                                await this.getAPIToken().then(async () => {
                                    await this.getQueueMembers()
                                })
                            }
                        }
                    } else {
                        this.queueMembers = data
                        this.enabledListeners = { ...this.enabledListeners, queueMembers: true };
                    }
                })
                .catch(error => console.error('Erro:', error));
        },

        async getPeersStatus() {
            this.enabledListeners = { ...this.enabledListeners, peerStatus: false };
            fetch(`${this.socketConnectionData.url}:${this.socketConnectionData.port}/sip-peers`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.socketConnectionData.token}`
                },
                credentials: 'include',
            })
                .then(response => response.json())
                .then(async data => {
                    if (data.error) {
                        if (data.error.includes('expirado') || data.error.includes('ausente')) {
                            if (!this.socketConnectionData.tokenRenewed) {
                                await this.getAPIToken().then(async () => {
                                    await this.getPeersStatus()
                                })
                            }
                        }
                    } else {
                        this.peersStatus = data
                        this.enabledListeners = { ...this.enabledListeners, peerStatus: true };
                    }
                })
                .catch(error => console.error('Erro:', error));
        },

        async setupSocketConnection() {
            if (this.socketClient && this.socketClient.connected) {
                this.socketClient.removeAllListeners();
                this.socketClient.disconnect();
            }
            this.socketClient = io.connect(`${location.protocol}//${location.hostname}:3009`, {
                reconnection: true,
                reconnectionDelay: 2000,
                reconnectionDelayMax: 10000,
                randomizationFactor: 0.5,
                pingTimeout: 600000,
                pingInterval: 120000,
                query: {
                    userId: user.getId(),
                    userName: user.getName()
                },
            });

            if (this.socketClient) {
                this.socketClient.on('connect', async () => {
                    this.reconnection = false
                    if (this.connectionCheck) {
                        console.info('[Operator Realtime] Reconexão ao server');

                        // *** LIMPAR PAUSAS ATIVAS NA RECONEXÃO ***
                        this.activePauses = {};

                        await this.getUsersList()
                        await this.getQueueCalls()
                        await this.getCurrentCalls()
                        await this.getQueueMembers()
                        await this.getPeersStatus()
                    } else {
                        console.info('[Operator Realtime] Nova conexão ao server');
                        this.connectionCheck = true
                    }
                });

                this.socketClient.on('disconnect', (reason) => {
                    console.info(`Desconectado: ${reason}`);
                    this.reconnection = true
                    this.usersList = []
                    this.currentCalls = []
                    this.queueMembers = []
                    this.peersStatus = []
                    this.queueCalls = []
                    this.enabledListeners = {
                        queueCalls: false,
                        currentCalls: false,
                        queueMembers: false,
                        peerStatus: false,
                    }
                });

                this.socketClient.on('reconnect_attempt', (attemptNumber) => {
                    console.info(`Tentativa de reconexão #${attemptNumber}`);
                });
                this.socketClient.on('CoreShowChannel', (data) => {
                    console.log('[CoreShowChannel]', data);
                    this.getCurrentCalls();
                });

                this.socketClient.on('NewConnectedLine', (data) => {
                    console.log('[NewConnectedLine]', data);
                    this.getCurrentCalls();
                });

                this.socketClient.on('Hangup', (data) => {
                    console.log('[Hangup]', data);
                    this.getCurrentCalls();
                });

                this.socketClient.on('PeerStatus', (data) => {
                    console.log('[PeerStatus]', data);
                    this.getPeersStatus();
                });
            }
        },
        updateCallsCount(count) {
            this.queueCallsCount = count
        },
        async eventEmitter(data){
    if (this.socketClient && this.socketClient.connected){
        this.socketClient.emit(data.event, data.payload)
    }

    if(data.event === 'pause'){
        if(data.payload.queues.length){
            const userId = data.payload.id;
            const currentTime = moment().format('YYYY-MM-DD HH:mm:ss');
            
            // *** VERIFICAR SE HÁ PAUSAS ATIVAS ***
            const currentlyPausedQueues = this.activePauses[userId]?.queues || [];
            
            // *** ENCONTRAR APENAS AS FILAS QUE JÁ ESTAVAM PAUSADAS E ESTÃO SENDO PAUSADAS NOVAMENTE ***
            const queuesToUnpause = currentlyPausedQueues.filter(queue => 
                data.payload.queues.includes(queue)
            );
            
            // *** SE HOUVER FILAS PARA DAR UNPAUSE, FAZER ISSO PRIMEIRO E AGUARDAR ***
            if(queuesToUnpause.length > 0){
                console.log(`[UNPAUSE ANTERIOR] Usuário ${userId} - Filas: ${queuesToUnpause.join(', ')}`);
                
                const unpauseObj = {
                    tempo: currentTime,
                    queues: [...queuesToUnpause],
                    userId: userId,
                };
                
                // *** AGUARDAR O UNPAUSE COMPLETAR ***
                await this.sendOperatorUnpause(unpauseObj);
                
                console.log('[UNPAUSE CONCLUÍDO] Aguardando antes de criar nova pausa...');
                
                // *** DELAY DE 200ms PARA GARANTIR QUE O BANCO PROCESSOU ***
                await new Promise(resolve => setTimeout(resolve, 200));
            }
            
            // *** AGORA REGISTRAR A NOVA PAUSA ***
            console.log(`[NOVA PAUSA] Usuário ${userId} - Filas: ${data.payload.queues.join(', ')} - Motivo: ${data.payload.reason}`);
            
            const pauseObj = {
                pauseId: data.payload.reason,
                tempo: currentTime,
                queues: [...data.payload.queues],
                userId: userId,
            };
            
            await this.sendOperatorPauseApiRealtime(pauseObj);
            
            // *** ATUALIZAR O RASTREAMENTO ***
            const remainingQueues = currentlyPausedQueues.filter(queue => 
                !data.payload.queues.includes(queue)
            );
            const allActiveQueues = [...new Set([...remainingQueues, ...data.payload.queues])];
            
            this.activePauses[userId] = {
                queues: allActiveQueues,
                reason: data.payload.reason
            };
            
            console.log(`[ATIVO] Usuário ${userId}:`, this.activePauses[userId]);
        }
    } else if(data.event === 'unpause'){
        if(data.payload.queues.length){
            const userId = data.payload.id;
            
            console.log(`[UNPAUSE] Usuário ${userId} - Filas: ${data.payload.queues.join(', ')}`);
            
            const obj = {
                tempo: moment().format('YYYY-MM-DD HH:mm:ss'),
                queues: [...data.payload.queues],
                userId: userId,
            }
            
            const unpauseResp = await this.sendOperatorUnpause(obj);
            if (unpauseResp && unpauseResp.success === false) {
                console.error('[UNPAUSE] Falha ao registrar retorno de pausa:', unpauseResp);
            }
            
            // *** REMOVER APENAS AS FILAS QUE FORAM DESPAUSADAS ***
            if(this.activePauses[userId]){
                const remainingQueues = this.activePauses[userId].queues.filter(
                    queue => !data.payload.queues.includes(queue)
                );
                
                if(remainingQueues.length > 0){
                    this.activePauses[userId].queues = remainingQueues;
                } else {
                    delete this.activePauses[userId];
                }
            }
            
            console.log(`[ATUALIZADO] Usuário ${userId} - activePauses:`, this.activePauses[userId] || 'vazio');
        }
    }
}
    },
    watch: {
        getStoreQueues(val) {
            this.queueList = val
        }
    }
}
</script>
